import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import ClModal from '../ClModal';
import { CreateElement, VNodeData, VNode } from 'vue';
import ClPaper from '../ClPaper/ClPaper';
import ClSlide from '../ClSlide/ClSlide';

type Variant = 'permanent' | 'persistent' | 'temporary';
type Anchor = 'top' | 'right' | 'bottom' | 'left';

const oppositeDirection: { [index: string]: string } = {
  left: 'right',
  right: 'left',
  top: 'down',
  bottom: 'up'
};

@Component({ components: { ClModal } })
export default class ClDrawer extends Vue {
  /**
   * Side from which the drawer will appear.
   */
  @Prop({
    type: String as () => Anchor,
    default: 'left'
  })
  public anchor!: Anchor;

  @Prop({
    type: Object,
    default: () => ({})
  })
  public clBackdropProps!: object;

  @Prop({
    type: Object,
    default: () => ({})
  })
  public classes!: object;

  /**
   * The elevation of the drawer
   */
  @Prop({
    type: Number,
    default: 16
  })
  public elevation!: number;

  /**
   * Properties applied to cl-modal
   */
  @Prop({
    type: Object,
    default: () => ({})
  })
  public clModalProps!: object;

  /**
   * If `true` the drawer is open
   */
  @Prop({
    type: Boolean,
    default: false
  })
  public open!: boolean;

  private computedOpen: boolean = this.open;

  @Prop({
    type: Object,
    default: () => ({})
  })
  public clPaperProps!: object;

  @Prop({
    type: Object,
    default: () => ({})
  })
  public clSlideProps!: object;

  /**
   * The variant to use
   */
  @Prop({
    type: String as () => Variant,
    default: 'temporary',
    validator: x => ['permanent', 'persistent', 'temporary'].includes(x)
  })
  public variant!: string;

  @Watch('open')
  watchOpenChange(newVal: boolean) {
    this.computedOpen = newVal;
  }

  render(h: CreateElement): VNode {
    const isTemp = this.variant === 'temporary';
    const drawerData: VNodeData = {
      class: {
        'cl-drawer__paper': true,
        [`cl-drawer__paper--anchor-${this.anchor}`]: true,
        [`cl-drawer__paper--anchor-${this.anchor}--docked`]: !isTemp
      },
      props: {
        square: true,
        elevation: isTemp ? this.elevation : 0,
        ...this.clPaperProps
      }
    };
    const drawer = h(ClPaper, drawerData, [this.$slots.default]);

    if (this.variant === 'permanent') {
      const permanentDrawerData: VNodeData = {
        class: {
          'cl-drawer': true,
          'cl-drawer--docked': true
        }
      };
      return h('div', permanentDrawerData, [drawer]);
    }

    const slidingDrawerData: VNodeData = {
      props: {
        in: this.computedOpen,
        direction: oppositeDirection[this.anchor],
        ...this.clSlideProps
      }
    };
    const slidingDrawer = h(ClSlide, slidingDrawerData, [drawer]);

    if (this.variant === 'persistent') {
      const persistentDrawerData: VNodeData = {
        class: {
          'cl-drawer': true,
          'cl-drawer--docked': true
        }
      };
      return h('div', persistentDrawerData, [slidingDrawer]);
    }

    // this.variant === 'temporary'
    const modalData: VNodeData = {
      class: {
        'cl-drawer': true,
        'cl-drawer__modal': true
      },
      props: {
        open: this.computedOpen,
        clBackdropProps: this.clBackdropProps
      },
      on: {
        close: () => {
          this.$emit('close');
        }
      }
    };
    return h(ClModal, modalData, [slidingDrawer]);
  }
}
